import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import SplitPane from 'react-split-pane'
import { message, Button, Alert } from 'antd'
import cn from 'classnames'
import { ListError } from '../Common'
import PDFPreview from '../Common/PDFPreview'
import api from '../../api'
import { dataURLToFile } from '../../lib/file'
import { useAsync } from '../../hooks'
import useInterval from '../../hooks/useInterval'
import Loading from '../Loading'
import ReportPatientHeader from '../Reports/ReportPatientHeader'
import { toPath } from '../../routes'
import useQueryParams from '../../hooks/useQueryParams'
import useSessionStorage from '../../hooks/useSessionStorage'
import ReportForm from './ReportForm'
import ReportUpload from './ReportUpload'

const POLLING_INTERVAL = 3000

const useStyles = createUseStyles({
  app: { display: 'flex', alignItems: 'stretch', flex: '1 1 auto' },
  pdfBox: {
    maxWidth: 'calc(100% - 456px)',
    flexGrow: '1',
    minWidth: '100%',
    position: 'relative',
    height: 'calc(100vh - 96px - 68px)',
    overflowY: 'scroll',
    overflowX: 'hidden',
    '& canvas': {
      maxWidth: '100%',
    },
  },
  formBox: {
    position: 'relative',
    overflowY: 'scroll',
    height: 'calc(100vh - 96px - 68px)',
    padding: '0 16px',
    '& > form': {
      marginTop: '-16px',
    },
  },
  reuploadContainer: {
    marginBottom: '1em',
    padding: '0 24px',
  },
  toggle: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
})

export default function EditOfficeCheck() {
  const { id, practiceId } = useParams()
  const [isUploaderVisible, setIsUploaderVisible] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isPollingPdfExtraction, setIsPollingPdfExtraction] = useState(false)
  const [, setPollingRetryCount] = useState(0)
  const [query] = useQueryParams({})
  const { getValue, deleteValue } = useSessionStorage()
  const filesQueryParam = query?.files
  const processedFilesRef = useRef(null)
  const [processingMessage, setProcessingMessage] = useState(
    'Processing device report...',
  )

  const getReportForm = useCallback(
    () => api.officeChecks.getReportForm(id, practiceId),
    [id, practiceId],
  )

  const {
    error,
    pending: loading,
    value: reportForm,
    execute: refetchReportForm,
  } = useAsync(getReportForm)
  const manufacturer = reportForm?.device?.deviceModel?.manufacturer

  const stopPollingAndReset = useCallback(() => {
    setIsPollingPdfExtraction(false)
    setPollingRetryCount(0)
  }, [])

  const handleRetryCount = useCallback(onMaxRetries => {
    setPollingRetryCount(prevCount => {
      const newCount = prevCount + 1
      if (newCount === 10) {
        setProcessingMessage(
          'This is taking a bit longer than expected. Please wait a few more seconds.',
        )
      } else if (newCount >= 20) {
        onMaxRetries()
      }
      return newCount
    })
  }, [])

  const showExtractionFailure = useCallback(() => {
    stopPollingAndReset()
    message.error('PDF extraction failed. Please fill out the form manually.')
  }, [stopPollingAndReset])

  const checkPdfExtractionStatus = useCallback(async () => {
    try {
      const officeCheck = await api.officeChecks.get(practiceId, id)
      const status = officeCheck.pdfExtractionStatus

      if (status === 'complete') {
        stopPollingAndReset()
        message.success('PDF extraction completed successfully')
        refetchReportForm()
      } else if (status === 'failed') {
        showExtractionFailure()
      } else {
        handleRetryCount(showExtractionFailure)
      }
    } catch (err) {
      handleRetryCount(showExtractionFailure)
    }
  }, [
    id,
    practiceId,
    refetchReportForm,
    stopPollingAndReset,
    showExtractionFailure,
    handleRetryCount,
  ])

  useInterval(
    checkPdfExtractionStatus,
    isPollingPdfExtraction ? POLLING_INTERVAL : null,
  )

  const backPath = () => {
    const path = toPath.practices.officeChecks.officeCheck({
      id: reportForm.id,
      practiceId,
    })

    const queryString = new URLSearchParams(query).toString()

    return `${path}?${queryString}`
  }

  useEffect(() => {
    setIsUploaderVisible(!reportForm?.deviceReport)
  }, [reportForm])

  const onUpload = useCallback(
    async (files, noReload = false, startPolling = false) => {
      const fd = new FormData()

      files.forEach(f => fd.append('attachment[]', f))

      try {
        await api.officeChecks
          .processDeviceReport(id, practiceId, fd)
          .then(response => {
            if (startPolling && response.pdfExtractionStatus !== 'skipped') {
              setPollingRetryCount(0) // Reset retry count when starting new polling
              setIsPollingPdfExtraction(true)
            } else if (!noReload) {
              window.location.reload(false)
            }
          })
      } catch (e) {
        message.error(`We got an error trying to process this file.`)
        setIsPollingPdfExtraction(false)
        setPollingRetryCount(0)
      }
    },
    [id, practiceId],
  )

  const handleSessionUpload = useCallback(
    (sessionKey, manuf) => {
      const files = getValue(sessionKey)
      if (files) {
        const restoredFiles = files
          // Discard log files if the device isn't Abbott
          .filter(
            file =>
              !file.name.endsWith('.log') || manuf.toLowerCase() === 'abbott',
          )
          .map(file => dataURLToFile(file.data, file.name))
        onUpload(restoredFiles, true, true)
          .then(() => {
            setIsUploading(false)
            deleteValue(query.files)
            const url = new URL(window.location)
            url.searchParams.delete('files')
            window.history.replaceState(null, '', url.toString())
          })
          .catch(e => {
            message.error('We got an error trying to process this file.')
            setIsUploading(false)
            setIsPollingPdfExtraction(false)
            setPollingRetryCount(0)
          })
      }
    },
    [deleteValue, getValue, onUpload, query.files],
  )

  useEffect(() => {
    if (
      filesQueryParam &&
      !isUploading &&
      manufacturer &&
      processedFilesRef.current !== filesQueryParam
    ) {
      processedFilesRef.current = filesQueryParam
      setIsUploading(true)
      handleSessionUpload(filesQueryParam, manufacturer)
    }
  }, [filesQueryParam, isUploading, manufacturer, handleSessionUpload])

  const classes = useStyles()

  if (loading) {
    return <Loading large />
  }

  if (isPollingPdfExtraction) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '50vh',
        }}
      >
        <Loading large />
        <div style={{ marginTop: '16px', fontSize: '16px', color: '#666' }}>
          {processingMessage}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <ListError
        message="There was an error loading this office check report"
        description={error.message}
      />
    )
  }

  return (
    <div className={classes.app}>
      <SplitPane split="vertical">
        <div initialSize="65%" minSize="10%" className={classes.pdfBox}>
          <ReportPatientHeader report={reportForm} backPath={backPath()} />
          {reportForm.deviceReport && (
            <div className={cn([classes.reuploadContainer, classes.toggle])}>
              {!isUploaderVisible && (
                <Button onClick={() => setIsUploaderVisible(true)}>
                  Upload Additional File
                </Button>
              )}
              {isUploaderVisible && (
                <Button onClick={() => setIsUploaderVisible(false)}>
                  Cancel
                </Button>
              )}
            </div>
          )}
          {isUploaderVisible && (
            <div className={classes.reuploadContainer}>
              <Alert
                message="Please verify populated fields for accuracy, as discrete data from the device may not match the PDF exactly."
                banner
                closable
                style={{ marginBottom: '1em' }}
              />

              <ReportUpload onUpload={onUpload} manufacturer={manufacturer} />
            </div>
          )}
          {reportForm.deviceReport && (
            <PDFPreview path={reportForm.deviceReport} showAllPages />
          )}
        </div>
        <div className={classes.formBox}>
          <ReportForm
            report={reportForm}
            practiceId={practiceId}
            // ENG-304: disable validation on form fields
            noValidation
          />
        </div>
      </SplitPane>
    </div>
  )
}
